import { css } from '@emotion/react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import PageContainer from '../../components/PageContainer';
import Button from '../../components/Button';
import PasswordField from '../../components/PasswordField';
import PasswordRequirementsValidator from '../../components/PasswordRequirementsValidator';
import InsecurePasswordMessage from '../../components/InsecurePasswordMessage';
import {
    getErrorMessage,
    getFirstError,
    navigateToErrorPage,
    showErrorNotificationFromResponse,
} from '../../services/errorUtils';
import ResponseStatus from '../../constants/ResponseStatus';
import ResponseErrorTypes from '../../constants/ResponseErrorTypes';
import useMessageService from '../../services/i18n/useMessageService';
import useRequestState from '../../services/useRequestState';
import { register } from '../../veevaIdApi';
import useNetworkErrorNotification from '../../services/useNetworkErrorNotification';
import useChangePassword from '../../services/useChangePassword';
import LoadingScreenContext from '../../services/LoadingScreenContext';
import UnauthenticatedRouteLanguageContext from '../../services/UnauthenticatedRouteLanguageContext';
import buildAndSubmitForm from '../../services/buildAndSubmitForm';
import RegistrationTypes from '../../constants/RegistrationTypes';
import RegisterConfirmation from './RegisterConfirmation';
import getTargetAttribute from '../../services/getTargetAttribute';
import convertStringToBoolean from '../../services/convertStringToBoolean';

const RegisterPasswordSetter = ({ firstName, language, lastName, locale, timezone }) => {
    const history = useHistory();
    const { setLoading } = useContext(LoadingScreenContext);
    const { messageLanguage } = useContext(UnauthenticatedRouteLanguageContext);
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [allRequirementsMet, setAllRequirementsMet] = useState(false);
    const { showNotification } = useNetworkErrorNotification();
    const { newPassword, setNewPassword, confirmedPassword, setConfirmedPassword } =
        useChangePassword();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const requestState = useRequestState();
    const handleConfirmRef = useRef();

    const [
        [
            setPasswordLabel = 'Set Your Password',
            joinNowLabel = 'Join Now',
            enterPasswordPlaceholder = 'Enter Password',
            confirmPasswordPlaceholder = 'Confirm Password',
        ],
    ] = useMessageService([
        'register.set_password',
        'register.join_now',
        'password.enter_password_placeholder',
        'password.confirm_password_placeholder',
    ]);

    useEffect(() => {
        setNewPasswordError('');
        setConfirmPasswordError('');
    }, [messageLanguage]);

    const handlePasswordChange = useCallback(({ valid, inlineError }) => {
        setAllRequirementsMet(valid && !inlineError);
        setNewPasswordError(inlineError);
        setConfirmPasswordError('');
    }, []);

    const checkForRegistrationFailures = async (response) => {
        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS: {
                return false;
            }
            case ResponseStatus.FAILURE: {
                const error = getFirstError(response);
                const errorMessage = await getErrorMessage(error, messageLanguage);

                switch (error.type) {
                    case ResponseErrorTypes.VEEVAID_INVALID_NEW_PASSWORD: {
                        setNewPasswordError(errorMessage);
                        break;
                    }
                    case ResponseErrorTypes.VEEVAID_INSECURE_PASSWORD: {
                        setConfirmPasswordError(<InsecurePasswordMessage />);
                        break;
                    }
                    case ResponseErrorTypes.VEEVAID_REGISTRATION_LINK_INVALID: {
                        navigateToErrorPage(history, error);
                        break;
                    }
                    default: {
                        const i18nOptions = { language, locale, timezone };
                        await showErrorNotificationFromResponse(response, i18nOptions);
                    }
                }
                return true;
            }
            case ResponseStatus.UI_NETWORK_ERROR:
            default: {
                await showNotification();
                return response.responseStatus !== ResponseStatus.SUCCESS;
            }
        }
    };

    const submitRegistration = (response) => {
        const { targetUrl, appRegToken, registrationType, useInterstitial, state } = response.data;
        const shouldUseInterstitial =
            useInterstitial === undefined || convertStringToBoolean(useInterstitial);
        const applicationRegistration =
            registrationType === RegistrationTypes.VAULT_APP ||
            registrationType === RegistrationTypes.NON_VAULT_APP;

        if (applicationRegistration) {
            const formParams = { appRegToken, language, state };
            if (shouldUseInterstitial) {
                handleConfirmRef.current = () => {
                    buildAndSubmitForm(targetUrl, formParams);
                };
            } else {
                // apps chose to skip the confirmation page
                buildAndSubmitForm(targetUrl, formParams);
            }
        } else if (shouldUseInterstitial) {
            handleConfirmRef.current = () => {
                window.location.href = targetUrl;
            };
        } else {
            window.location.href = targetUrl;
        }

        setShowConfirmation(shouldUseInterstitial);
    };

    const handleJoinNowButtonClick = async () => {
        setLoading(true);
        const user = {
            password: newPassword,
            firstName,
            lastName,
            language,
            locale,
            timezone,
        };
        const response = await register(user, requestState);

        const failedRegistration = await checkForRegistrationFailures(response);
        if (failedRegistration) {
            setLoading(false);
            return;
        }

        submitRegistration(response);
        setLoading(false);
    };

    if (showConfirmation) {
        return <RegisterConfirmation onConfirm={handleConfirmRef?.current} />;
    }

    return (
        <PageContainer title={setPasswordLabel}>
            <PasswordField
                onChange={(value) => setNewPassword(value)}
                value={newPassword}
                placeholder={enterPasswordPlaceholder}
                errorMessage={newPasswordError}
                error={!!confirmPasswordError}
            />
            <PasswordField
                onChange={(value) => setConfirmedPassword(value)}
                value={confirmedPassword}
                placeholder={confirmPasswordPlaceholder}
                errorMessage={confirmPasswordError}
            />
            <PasswordRequirementsValidator
                password={newPassword}
                passwordConfirmation={confirmedPassword}
                onStateChange={handlePasswordChange}
            />

            <Button
                css={css`
                    margin-top: 3.333rem;
                `}
                onClick={handleJoinNowButtonClick}
                disabled={!allRequirementsMet}
                data-target={getTargetAttribute('submit-button')}
            >
                {joinNowLabel ?? ' '}
            </Button>
        </PageContainer>
    );
};

RegisterPasswordSetter.propTypes = {
    firstName: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
};

export default RegisterPasswordSetter;
